// CAT-02 (doc 20 §5.2, R20.4 / R20.5). Pure allergen-state resolution.
//
// This module is the heart of the allergen-safety fix. It replaces the unsafe
// "null/empty allergen list == safe" default (defect M4 / P1) with a three-valued
// model where "we don't know" is its own explicit, never-safe state.
//
// Pure: no I/O, no server imports. Safe to call from client, server, and tests.

export type AllergenAssessment = 'declared' | 'none_present' | 'not_assessed';

export interface AllergenInput {
  /** The merchant-declared allergen list (`menu_items.allergens`). null/undefined = never set. */
  allergens?: string[] | null;
  /** True once the merchant has explicitly confirmed the allergen info (CAT-02). */
  declared?: boolean;
}

export interface ItemAllergenState {
  assessment: AllergenAssessment;
  /** The confirmed allergen list (always the declared list, never derived). */
  allergens: string[];
  /** Allergens detected from the recipe AND present in the declared list (R20.5). */
  derivedConfirmed: string[];
  /** Allergens detected from the recipe but NOT in the declared list (R20.5). */
  derivedUnconfirmed: string[];
}

/**
 * R20.4 — resolve the three-valued allergen assessment for an item.
 *
 * `not_assessed` is the default whenever the merchant has not explicitly
 * declared; it must render as a caution, never as an empty safe list. The
 * declared flag is the source of truth: an empty list with `declared = false`
 * is still `not_assessed`, so legacy/un-reviewed items can never masquerade as
 * "no allergens".
 */
export function resolveAllergenAssessment(input: AllergenInput): AllergenAssessment {
  const declared = input.declared ?? false;
  if (!declared) return 'not_assessed';
  const list = Array.isArray(input.allergens) ? input.allergens : [];
  return list.length > 0 ? 'declared' : 'none_present';
}

/** True when the item's allergen state is unknown and must NOT be shown as safe. */
export function isAllergenNotAssessed(input: AllergenInput): boolean {
  return resolveAllergenAssessment(input) === 'not_assessed';
}

/**
 * R20.5 — split recipe-derived allergens into confirmed vs unconfirmed against
 * the merchant-declared list. A derived allergen the merchant also listed is a
 * confirmation; a derived allergen the merchant has NOT listed is a strong
 * signal we surface as "not yet confirmed" — never auto-published as safe.
 */
export function classifyDerivedAllergens(
  declared: string[] | null | undefined,
  derived: string[] | null | undefined,
): { derivedConfirmed: string[]; derivedUnconfirmed: string[] } {
  const declaredSet = new Set((declared ?? []).map((a) => a.toLowerCase()));
  const derivedConfirmed: string[] = [];
  const derivedUnconfirmed: string[] = [];
  for (const d of derived ?? []) {
    if (declaredSet.has(d.toLowerCase())) derivedConfirmed.push(d);
    else derivedUnconfirmed.push(d);
  }
  return { derivedConfirmed, derivedUnconfirmed };
}

/** Build the full per-item allergen state used by the public menu payload. */
export function buildItemAllergenState(
  input: AllergenInput & { derived?: string[] | null },
): ItemAllergenState {
  const assessment = resolveAllergenAssessment(input);
  const allergens = Array.isArray(input.allergens) ? input.allergens : [];
  const { derivedConfirmed, derivedUnconfirmed } = classifyDerivedAllergens(allergens, input.derived ?? null);
  return { assessment, allergens, derivedConfirmed, derivedUnconfirmed };
}

/** Guest-facing caution copy for a not-assessed item (R20.4). Never an empty safe list. */
export const NOT_ASSESSED_COPY = 'Allergen information not available — ask staff';

/** Map the doc's three-valued assessment to the legacy P-09.4a status vocabulary. */
export function toLegacyAllergenStatus(assessment: AllergenAssessment): 'assessed_contains' | 'assessed_none' | 'not_assessed' {
  switch (assessment) {
    case 'declared':
      return 'assessed_contains';
    case 'none_present':
      return 'assessed_none';
    case 'not_assessed':
      return 'not_assessed';
  }
}

/**
 * Read the assessment off a serialized item, tolerating both the new canonical
 * `allergen_assessment` field and the legacy `allergen_status` vocabulary, with a
 * last-resort inference from list length. Guarantees a valid three-valued result.
 */
export function assessmentFromItem(item: {
  allergen_assessment?: AllergenAssessment | null;
  allergen_status?: 'assessed_contains' | 'assessed_none' | 'not_assessed' | null;
  allergens?: string[] | null;
}): AllergenAssessment {
  if (item.allergen_assessment) return item.allergen_assessment;
  if (item.allergen_status) {
    if (item.allergen_status === 'assessed_contains') return 'declared';
    if (item.allergen_status === 'assessed_none') return 'none_present';
    return 'not_assessed';
  }
  return (item.allergens?.length ?? 0) > 0 ? 'declared' : 'not_assessed';
}
